import { useCallback, useEffect, useMemo, useState } from 'react';

import { GetAssetLocationsRequestDTO, GetAssetLocationsResponseDTO, MODULE, PdfScope } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';

import maintenanceVector from '@/assets/vectors/manhole-shrug.svg';
import { ColumnsIcon } from '@/components/icons/columns';
import DownloadIcon from '@/components/icons/download';
import UndoIcon from '@/components/icons/undo';
import CreateLocationLogDrawer from '@/components/location-logs/create-location-log-drawer';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { getLocationLogsInfiniteQueryOptions, locationLogKeys } from '@/queries/locations';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { columns, FILTER_FIELD_MAP, GROUP_OPTIONS } from './table-meta';

function LocationLogsPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();

  const { setModuleFilters, syncModuleColumns } = useModulesStore();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.LocationLogs, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions: GROUP_OPTIONS,
    columns,
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    syncModuleColumns(MODULE.LocationLogs, columns);
  }, [syncModuleColumns]);

  const {
    data: locationLogsData,
    fetchNextPage: fetchNextLocationLogsPage,
    hasNextPage: hasNextLocationLogsPage,
    isFetching: isFetchingLocationLogs,
    isFetchingNextPage: isFetchingNextLocationLogsPage,
    error: locationLogsError,
  } = useInfiniteQuery(
    getLocationLogsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch || undefined,
        ...buildFilterFields(filters, FILTER_FIELD_MAP),
      },
      groupBy: grouping.length
        ? {
            field: grouping[0] as NonNullable<GetAssetLocationsRequestDTO['groupBy']>['field'],
            direction: (sorting.direction || 'asc') as 'asc' | 'desc',
          }
        : undefined,
      orderBy: sorting.key
        ? {
            field: sorting.key as NonNullable<GetAssetLocationsRequestDTO['orderBy']>['field'],
            direction: sorting.direction as 'asc' | 'desc',
          }
        : {
            field: 'createdAt',
            direction: 'desc',
          },
      enabled: !isLoadingPageConfig,
    }),
  );

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    if (locationLogsError) {
      toast.error(locationLogsError.message);
    }
  }, [toast, locationLogsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextLocationLogsPage,
    hasNextPage: hasNextLocationLogsPage,
    isFetching: isFetchingLocationLogs,
    isError: !!locationLogsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => locationLogsData?.pages.flatMap((page) => page.data) ?? [], [locationLogsData]);

  const selectedLocationLogs = useMemo(() => {
    const ids = Object.keys(selectedRows);
    return ids.filter((id) => !isNaN(parseInt(id))).map((id) => flattenedData[parseInt(id)]);
  }, [selectedRows, flattenedData]);

  const { canExportCsv } = usePermissions();
  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  const buildLocationLogsExportRequest = useCallback((): RegisterExportQuery<'location-logs'> => {
    return {
      queryType: 'location-logs',
      filters: { fields: { id: selectedLocationLogs.map((log) => log.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetAssetLocationsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetAssetLocationsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }, [columnOrder, selectedLocationLogs, grouping, sorting]);

  const handleDownload = useCallback(() => {
    openExportTab({ kind: 'csv', request: { ...buildLocationLogsExportRequest(), filename: buildCsvExportFilename('location-logs') } });
  }, [buildLocationLogsExportRequest]);

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!locationLogsData?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildLocationLogsExportRequest() }));
  }

  const isLoading = (isFetchingLocationLogs && !isFetchingNextLocationLogsPage) || isLoadingPageConfig;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedLocationLogs.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Location Logs</TablePageTitle>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.LocationLogs, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex h-full items-center">
                    <TablePageTableGroupDropdown
                      columns={GROUP_OPTIONS}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      icon={<ColumnsIcon />}
                      label="Edit Columns"
                      disabled={isLoadingPageConfig}
                      onClick={() => setOpenColumnsDialog(true)}
                    />
                  </div>
                  <CreateLocationLogDrawer
                    callback={() => {
                      queryClient.invalidateQueries({
                        queryKey: locationLogKeys.all,
                      });
                    }}
                  />
                </div>
              </>
            )}

            {selectedLocationLogs.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedLocationLogs.length} totalCount={locationLogsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadIcon />} label="Download CSV" onClick={handleDownload} />}
                {canExportCsv && isRegisterPdfExportEnabled && <DownloadPdfMenu onSelect={handlePdfScopeSelect} />}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <div className="flex-1 bg-white">
          <TablePageTable
            data={flattenedData}
            columns={columns}
            notFoundDisplay={
              isLoading ? null : debouncedSearch.length > 0 ? (
                <NoLocationLogsFoundFromSearchDisplay />
              ) : filters && filters.length > 0 ? (
                <NoLocationLogsFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.LocationLogs, [])} />
              ) : (
                <NoLocationLogsCreatedDisplay />
              )
            }
            loading={isLoading}
            onSortingChange={(sorting) =>
              updatePageConfig({
                table: { sorting },
              })
            }
            initialSorting={initialSorting}
            columnFilters={filters || []}
            onColumnFiltersChange={(filters) => setModuleFilters(MODULE.LocationLogs, filters)}
            grouping={grouping}
            columnOrder={['select', ...columnOrder.map((col) => col.id).filter((id) => id !== 'select')]}
            columnVisibility={{ ...columnVisibility, select: true }}
            rowSelection={selectedRows}
            onRowSelectionChange={setSelectedRows}
          />
        </div>
        {hasNextLocationLogsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextLocationLogsPage ? 'Loading more...' : 'Load More'}
          </Button>
        )}
      </ScrollArea>

      <ColumnsDialog<GetAssetLocationsResponseDTO['data'][number]>
        open={openColumnsDialog}
        onOpenChange={setOpenColumnsDialog}
        columns={columns}
        columnOrder={columnOrder}
        onColumnOrderChange={(columnOrder) =>
          updatePageConfig({
            table: { columnOrder },
          })
        }
        disabled={isLoadingPageConfig}
        noTrigger
      />
    </div>
  );
}

function NoLocationLogsCreatedDisplay() {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={maintenanceVector} alt="LocationLog" className="h-[170px] w-[250px]" />
      <span className="text-center text-lg leading-[30px] font-medium">No location logs yet.</span>
      <div className="text-secondary-text pointer-events-auto text-center">Location logs will appear here as locations are recorded across assets.</div>
    </div>
  );
}

function NoLocationLogsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={maintenanceVector} alt="LocationLog" className="h-28" />
      <span className="text-xl font-medium">No records found</span>
      <span className="text-secondary-text text-sm">Try adjusting your search terms.</span>
    </div>
  );
}

function NoLocationLogsFoundFromFiltersDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={maintenanceVector} alt="LocationLog" className="h-28" />
      <span className="text-xl font-medium">No records found</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters or creating a location log.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}

export default LocationLogsPage;
